import { Map } from 'immutable';
import { getLearnerDocument } from '../learnerDocument';

const CompetencyProvider = (() => {
  let _competencyMap = Map();
  let _documentItemsMap = Map();
  let _documentMap = Map();
  let _documents;

  function _linkCompetencyToCoursewareitem(itemId, coursewareItemId) {
    const competencyForCoursewareItem = _competencyMap.get(coursewareItemId, []);
    const competencyList = competencyForCoursewareItem.filter((id) => id !== itemId);
    competencyList.push(itemId);
    _competencyMap = _competencyMap.set(coursewareItemId, competencyList);
  }

  async function _requestDocumentFromNetwork(documentId) {
    const result = await getLearnerDocument({ documentId });
    const { data } = result || {};
    const { learn } = data || {};
    const { learnerDocument } = learn || {};
    return learnerDocument;
  }

  function _processDocumentItemAssociation(association) {
    const {
      originItemId,
      destinationItemId,
      associationType,
      associationId,
    } = association;

    return {
      originItemId,
      destinationItemId,
      associationType,
      associationId,
    };
  }

  function _processDocumentItem(documentItem) {
    const {
      id,
      documentId,
      fullStatement,
      abbreviatedStatement,
      humanCodingScheme,
      competencyDocumentItemMet,
      associationsTo,
      associationsFrom,
    } = documentItem;

    const { edges: associationsToEdges = [] } = associationsTo;
    const { edges: associationsFromEdges = [] } = associationsFrom;

    return {
      id,
      documentId,
      fullStatement,
      abbreviatedStatement,
      humanCodingScheme,
      competencyDocumentItemMet: competencyDocumentItemMet || undefined,
      associationsTo: associationsToEdges.map(
        (association) => _processDocumentItemAssociation(association.node)
      ),
      associationsFrom: associationsFromEdges.map(
        (association) => _processDocumentItemAssociation(association.node)
      ),
    };
  }

  function _processLearnerDocument(learnerDocument) {
    if (!learnerDocument) {
      return undefined;
    }

    const { documentId, title, documentItems } = learnerDocument;
    const { edges = [] } = documentItems;

    const processedDocumentItems = edges.map(
      (edge) => _processDocumentItem(edge.node)
    );

    return {
      learnerDocument: {
        documentId,
        title,
        documentItems: processedDocumentItems.map((item) => item.id),
      },
      documentItems: processedDocumentItems,
    };
  }

  async function loadLearnerDocument(documentId) {
    const networkDocument = await _requestDocumentFromNetwork(documentId);
    const processedDocument = _processLearnerDocument(networkDocument);
    if (processedDocument) {
      const { learnerDocument, documentItems } = processedDocument;
      _documentMap = _documentMap.set(learnerDocument.documentId, learnerDocument);
      _documentItemsMap = documentItems.reduce(
        (acc, documentItem) => acc.set(documentItem.id, documentItem),
        _documentItemsMap
      );
    }
  }

  function resolveAssociations(itemId, documentItemsMap) {
    const documentItem = documentItemsMap.get(itemId);
    if (!documentItem) {
      return undefined;
    }

    const associationsTo = documentItem.associationsTo.map((association) => {
      const { associationId, associationType, originItemId } = association;
      return {
        associationId,
        associationType,
        itemId: originItemId,
        item: documentItemsMap.get(originItemId),
      };
    });

    const associationsFrom = documentItem.associationsFrom.map((association) => {
      const { associationId, associationType, destinationItemId } = association;
      return {
        associationId,
        associationType,
        itemId: destinationItemId,
        item: documentItemsMap.get(destinationItemId),
      };
    });

    return {
      ...documentItem,
      associationsTo,
      associationsFrom,
    };
  }

  async function addCompetenciesForCoursewareItem(items = [], coursewareItemId) {
    let documentsResolver;
    _documents = new Promise((resolve) => { documentsResolver = (...args) => resolve(...args); });

    const documentIds = new Set(
      items.map((item) => item.documentId)
    );

    const documentPromises = Array.from(documentIds).map(
      (documentId) => loadLearnerDocument(documentId)
    );

    await Promise.all(documentPromises);

    items.forEach(
      ({ itemId }) => _linkCompetencyToCoursewareitem(itemId, coursewareItemId)
    );

    // TODO: remove the internal vars (_competencyMap, _documentItemsMap, _documentMap) altogether
    documentsResolver({
      competencyMap: _competencyMap,
      documentItemsMap: _documentItemsMap,
      documentMap: _documentMap,
    });
  }

  async function getCompetencyForCoursewareItem(coursewareItemId) {
    const { competencyMap, documentItemsMap } = await _documents;

    const documentItemIds = competencyMap.get(coursewareItemId);
    if (!documentItemIds) {
      return [];
    }
    const documentItems = documentItemIds.map(
      (documentItemId) => resolveAssociations(documentItemId, documentItemsMap)
    );
    return documentItems;
  }

  async function getCompetencyDocument(documentId) {
    const { documentMap, documentItemsMap } = await _documents;

    const learnerDocument = documentMap.get(documentId);
    if (!learnerDocument) {
      return undefined;
    }
    const { title, documentItems } = learnerDocument;
    return {
      title,
      documentItems: documentItems.map((documentItemId) => resolveAssociations(documentItemId, documentItemsMap)),
    };
  }

  async function getCompetencyDocumentItem(itemId) {
    const { documentItemsMap } = await _documents;
    return resolveAssociations(itemId, documentItemsMap);
  }

  return {
    loadLearnerDocument,
    addCompetenciesForCoursewareItem,
    getCompetencyForCoursewareItem,
    getCompetencyDocument,
    getCompetencyDocumentItem,
  };
})();

export default CompetencyProvider;
